import { inject, injectable } from "inversify";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useUserManagementStore } from "@presentation/stores";
import { TYPES } from "@utils/inversifyJS/types";
import { UserSession } from "@domain/entities/UserSession";
import type { IAuthenticatedRequestHandlerService, IAuthService } from "@application/contracts/services";

const userStore = useUserManagementStore.getState;

@injectable()
export class AuthenticatedRequestHandlerService implements IAuthenticatedRequestHandlerService {
  @inject(TYPES.IAuthService)
  private _authService!: IAuthService;

  /**
   * Wraps an axios request factory with auth handling:
   * 1) Ensure we have a valid token (buffer=0, only refresh if truly expired).
   * 2) Set Authorization header.
   * 3) Perform the request.
   * 4) If 401, re-check expiry and refresh only if expired, then retry once.
   *
   * Note: AuthService.refreshAccessToken updates the Zustand store, so we avoid redundant writes here.
   */
  public async handleRequestAsync<T>(request: () => Promise<AxiosResponse<T>>): Promise<T> {
    // Ensure we have a valid token or refresh if truly expired.
    await this.ensureValidTokenAsync();

    // Read the token from the store (may have been changed by refresh).
    let currentState = userStore();
    let accessToken = currentState.userSession?.accessToken ?? null;

    if (!accessToken) {
      // As a last resort: try one refresh and re-read.
      accessToken = await this._authService.refreshAccessToken();
      currentState = userStore();
      if (currentState.userSession?.accessToken !== accessToken) {
        useUserManagementStore.setState({
          userSession: new UserSession(currentState.userSession?.username ?? null, accessToken),
        });
      }
    }

    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;

    try {
      const response = await request();
      return response.data;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;

        if (status === 401) {
          // Re-check the token; only refresh if actually expired, to avoid 400 on refresh.
          const stateBeforeRetry = userStore();
          const tokenBeforeRetry = stateBeforeRetry.userSession?.accessToken ?? null;
          const isExpired = this._authService.isTokenExpired(tokenBeforeRetry);

          if (isExpired) {
            const newToken = await this._authService.refreshAccessToken();
            axios.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
            const retryResponse = await request();
            return retryResponse.data;
          }

          // Token is not expired; backend refused for another reason. Don't try refresh (would 400).
          throw err as AxiosError;
        }

        // Non-401 axios errors propagate as-is
        throw err as AxiosError;
      }

      // Non-axios unknown error
      if (err instanceof Error) {
        throw err;
      }
      throw new Error("Unknown error occurred during authenticated request.");
    }
  }

  /**
   * Helper: If token is missing or expired (buffer=0), call single-flight refresh.
   * Does not modify store directly; AuthService will update it.
   */
  private async ensureValidTokenAsync(): Promise<void> {
    let state = userStore();
    const token = state.userSession?.accessToken ?? null;

    if (!token || this._authService.isTokenExpired(token)) {
      await this._authService.refreshAccessToken();

      // After refresh, the store should be updated by AuthService.
      state = userStore();
      if (!state.userSession?.accessToken) {
        throw new Error("Failed to acquire a valid access token.");
      }
    }
  }
}
